import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ErrorMessage, useFormikContext } from 'formik';
import { Col, Row, Input } from 'syngenta-digital-cropwise-react-ui-kit';
import {
  convertDensityFromKgPerLToLbsPerGal,
  convertDensityFromLbsPerGalToKgPerL
} from 'screens/ProductList/helpers/convertDensity';
import { useUomPackage } from 'hooks/useUomConvert';
import FormInputWithPrecisionDisplay from 'screens/YieldV2/AddYield/components/FormInputWithPrecisionDisplay';
import PriceUnitField from 'components/PriceUnitField';
import { UNITS } from '../EditProductDrawer/editCustomProductUnits';
import productValidationAlert from '../../../../assets/productValidationAlert.svg';
import '../EditProductDrawer/index';
import FormulationFields from './components/FormulationFields';
import SelectField from './components/SelectField';

export const handleValidationAlertIcon = (error, field) => {
  return error?.[field] ? <img src={productValidationAlert} alt="" /> : null;
};

const CustomProductListForm = ({ product, setSchema }) => {
  const {
    values,
    setValues,
    setFieldValue,
    validateField,
    setFieldTouched,
    handleBlur,
    handleChange,
    errors,
    touched
  } = useFormikContext();
  const { Pound, Gallon } = useUomPackage();

  const unitList = UNITS?.[values.type];

  const formRowClass = 'mb-6';
  const formLabelClass = 'text-neutral-60 flex';

  useEffect(() => {
    if (!product.isNew) {
      setValues({
        ...product,
        densityInLbsPerGallon: convertDensityFromKgPerLToLbsPerGal(
          product.density,
          Pound,
          Gallon
        )
      });
    }
  }, [setValues, product, Pound, Gallon]);

  useEffect(() => {
    if (!values?.type) return;

    setSchema(values.type);
  }, [values.type, setSchema]);

  useEffect(() => {
    if (!values?.densityInLbsPerGallon) {
      setFieldValue('density');
      setFieldTouched('density');
      return;
    }
    setFieldValue(
      'density',
      convertDensityFromLbsPerGalToKgPerL(
        values.densityInLbsPerGallon,
        Pound,
        Gallon
      )
    );
    setFieldTouched('density');
    validateField('density');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.densityInLbsPerGallon, setFieldValue]);

  useEffect(() => {
    if (!values?.standardUnit || touched.averagePricePerUnitUnit) return;

    setFieldValue('averagePricePerUnitUnit', values.standardUnit);
    setFieldTouched('averagePricePerUnitUnit', true);
  }, [setFieldValue, setFieldTouched, touched, values]);

  return (
    <div className="pr-4">
      <Col>
        <Row className="mb-6 text-neutral-60">
          <span> Need a custom crop protection or seed product?&nbsp;</span>
          <a
            className="text-blue-60 underline"
            href="https://www.syngentadigital.com/us/product-request-form"
            target="_blank"
            rel="noreferrer"
          >
            Request here.
          </a>
        </Row>
      </Col>
      <div>
        <Row className={formRowClass} gutter={32}>
          <Col span={12}>
            <SelectField
              id="type"
              label="Product Type"
              required
              onChange={value => {
                setFieldValue('type', value);
                setFieldValue('averagePricePerUnitUnit', '', false);
                setFieldValue('standardUnit', '', false);
              }}
              onBlur={handleBlur}
              options={[
                { label: 'Fertilizer', value: 'FERTILIZER' },
                { label: 'Service', value: 'SERVICE' }
              ]}
              errorMessage={
                touched.type && errors.type ? errors.type : undefined
              }
              defaultValue={values.type}
              value={values.type}
            />
          </Col>
        </Row>
        <Row className={formRowClass} gutter={32}>
          <Col span={12}>
            <Input
              id="name"
              name="name"
              label="Product Name"
              required
              onChange={handleChange}
              onBlur={handleBlur}
              type={touched.name && errors.name ? 'danger' : 'default'}
              className="my-2px"
              defaultValue={values.name}
              value={values.name}
            />
            <ErrorMessage
              name="name"
              component="div"
              className="text-remove text-wrap"
            />
          </Col>
          <Col span={12}>
            <Input
              id="manufacturer"
              name="manufacturer"
              label="Manufacturer"
              required
              onChange={handleChange}
              onBlur={handleBlur}
              type={
                touched.manufacturer && errors.manufacturer
                  ? 'danger'
                  : 'default'
              }
              className="my-2px"
              defaultValue={values.manufacturer}
              value={values.manufacturer}
            />
            <ErrorMessage
              name="manufacturer"
              component="div"
              className="text-remove text-wrap"
            />
          </Col>
        </Row>
        {values.type === 'FERTILIZER' && (
          <Row className={formRowClass} gutter={32}>
            <Col span={12}>
              <SelectField
                id="makeUp"
                label="Make-Up"
                required
                onChange={value => setFieldValue('makeUp', value)}
                onBlur={handleBlur}
                defaultValue="SOLID"
                value={values.makeUp}
                options={[
                  { label: 'Solid', value: 'SOLID' },
                  { label: 'Liquid', value: 'LIQUID' }
                ]}
                errorMessage={
                  touched.makeUp && errors.makeUp ? errors.makeUp : undefined
                }
              />
            </Col>
            <Col span={12}>
              <label htmlFor="densityInLbsPerGallon" className={formLabelClass}>
                Density
                {values.makeUp === 'LIQUID' && (
                  <div className="text-remove">*</div>
                )}
              </label>
              <FormInputWithPrecisionDisplay
                id="densityInLbsPerGallon"
                fieldName="densityInLbsPerGallon"
                label={values.makeUp === 'LIQUID' ? 'lb/gal' : ''}
                inputSuffix={values.makeUp === 'LIQUID' ? 'lb/gal' : ''}
                isRequired={values.makeUp === 'LIQUID'}
                onControlledBlur={() =>
                  setFieldTouched('densityInLbsPerGallon', true)
                }
              />
              <ErrorMessage
                name="densityInLbsPerGallon"
                component="div"
                className="text-remove text-wrap"
              />
            </Col>
          </Row>
        )}
        <Row className={formRowClass} gutter={32}>
          <Col span={12}>
            <PriceUnitField
              key={values.type}
              units={unitList ?? []}
              customLabel={<div className={formLabelClass}>List Price</div>}
              required={false}
              fieldNameInput="averagePricePerUnit"
              fieldNameUnit="averagePricePerUnitUnit"
            />
            <ErrorMessage
              name="averagePricePerUnit"
              component="div"
              className="text-remove text-wrap"
            />
          </Col>

          <Col span={12}>
            <SelectField
              id="standardUnit"
              label="Standard Unit"
              required
              options={unitList ?? []}
              onChange={value => {
                setFieldValue('standardUnit', value, true);
              }}
              onBlur={handleBlur}
              errorMessage={
                touched.standardUnit && errors.standardUnit
                  ? errors.standardUnit
                  : undefined
              }
              defaultValue={values.standardUnit}
              value={values.standardUnit}
            />
          </Col>
        </Row>
        {values.type === 'FERTILIZER' && <FormulationFields />}
      </div>
    </div>
  );
};

CustomProductListForm.defaultProps = {
  setSchema: () => null
};

CustomProductListForm.propTypes = {
  product: PropTypes.shape().isRequired,
  setSchema: PropTypes.func
};

export default CustomProductListForm;
